/**
 * The admin's left rail: the eight sections, plus LIVE queue counters (docs/06 §4).
 *
 * The counters are the point. Merdan's two daily jobs are approving and pricing, and the sidebar
 * is where he learns there is work without navigating to look. They come from GET /admin/dashboard
 * and are invalidated by the `role:admin` websocket topic, so they move without an F5 (plans.md §7.4).
 *
 * Dead notifications get their own red badge even though "failed" already has one: a failed row is
 * still retrying, a dead row has given up — and a dead admin alert means an order arrived and
 * nobody was told.
 *
 * A counter is never colour alone. Amber means "work is waiting", red means "something is broken",
 * and the red ones carry a dot the amber ones do not — so the distinction survives both a
 * monochrome screenshot in a support chat and a red-green colour-blind operator (docs/06 §12).
 *
 * The rail is the can: true black, with the flavour colour glowing up from the bottom and
 * carbonation rising through it. Its one repeated shape is the logo's ring — every icon sits in a
 * circle, and the active section's circle fills with the flavour colour, the way the "o" does on
 * the label. See the notes at each.
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BellRing,
  ClipboardList,
  FolderTree,
  LayoutDashboard,
  MessageSquare,
  Settings as SettingsIcon,
  ShoppingCart,
  Tag,
  UserCheck,
  Users,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@most/ui';
import type { CSSProperties, ComponentType } from 'react';
import { BoldCan, BoldLogo, Bubbles } from '@/components/bold/Bold';
import { useChats, useDashboard } from '@/lib/queries';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  /** The live counter for this section, if it has one. */
  count?: number;
  /** A count that means "something is broken", not "something is waiting". */
  alarming?: boolean;
  end?: boolean;
}

/**
 * Saturated fills, bright enough to pull the eye on black. (The amber classes read "backwards"
 * here because the admin's amber scale is mirrored for the dark canvas — see tailwind.config.cjs —
 * so `amber-700` is the LIGHT amber and `amber-50` the near-black ink on it.)
 */
function Counter({ value, alarming }: { value: number; alarming?: boolean }) {
  if (!value) return null;

  return (
    <span
      className={cn(
        'tabular relative ml-auto flex h-5 min-w-[1.25rem] items-center justify-center gap-1 rounded-full px-1.5',
        'text-2xs font-bold tabular-nums',
        alarming
          ? 'bg-red-500 text-white shadow-[0_0_14px_rgb(239_68_68/0.7)]'
          : 'bg-amber-700 text-amber-50',
      )}
    >
      {/* The dot is what separates "broken" from "waiting" without colour; on an alarming
          counter it also breathes, so a red badge is noticed from the other side of the screen. */}
      {alarming ? (
        <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80 motion-reduce:hidden" />
          <span className="relative h-1.5 w-1.5 rounded-full bg-white/90" />
        </span>
      ) : null}
      {value > 99 ? '99+' : value}
    </span>
  );
}

/**
 * The active plate: a pill tinted with the flavour colour that fades out to the right, with a
 * lit hairline along its top edge — a strip of the can's label rather than a grey slab.
 */
const PLATE = cn(
  'absolute inset-0 rounded-full border border-white/10',
  'bg-gradient-to-r from-primary/35 via-primary/10 to-white/[0.03]',
  'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.12),0_8px_24px_-12px_hsl(var(--primary))]',
  'transition-colors duration-500',
);

export function Sidebar() {
  const { t } = useTranslation();
  const { data } = useDashboard();
  const { data: chats } = useChats();
  const reduce = useReducedMotion();

  const queues: NavItem[] = [
    { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
    {
      to: '/approvals',
      labelKey: 'nav.approvals',
      icon: UserCheck,
      count: data?.pending_approvals,
    },
    {
      to: '/pricing',
      labelKey: 'nav.pricing',
      icon: Tag,
      // Urgent products (unpriced → invisible to buyers right now) are the alarming subset, so
      // the pricing counter goes red the moment any of them exist.
      count: data?.unpriced_products,
      alarming: (data?.urgent_products ?? 0) > 0,
    },
    { to: '/orders', labelKey: 'nav.orders', icon: ShoppingCart, count: data?.new_orders },
    {
      to: '/inbox',
      labelKey: 'nav.inbox',
      icon: MessageSquare,
      // Unanswered support messages are a queue like any other — the count is users waiting.
      count: chats?.unread_count,
    },
  ];

  const manage: NavItem[] = [
    { to: '/users', labelKey: 'nav.users', icon: Users },
    { to: '/categories', labelKey: 'nav.categories', icon: FolderTree },
  ];

  const deadCount = data?.dead_notifications ?? 0;
  const failedCount = data?.failed_notifications ?? 0;

  const system: NavItem[] = [
    {
      to: '/notifications',
      labelKey: 'nav.notifications',
      icon: deadCount > 0 ? BellRing : ClipboardList,
      count: deadCount || failedCount,
      alarming: deadCount > 0,
    },
    // Settings gathers Monitoring, Roles & Permissions, and the (relocated) Audit log. NavLink
    // matches any /settings/* sub-tab, so the item stays active across all three.
    { to: '/settings', labelKey: 'nav.settings', icon: SettingsIcon },
  ];

  const renderGroup = (heading: string, items: NavItem[]) => (
    <div className="px-3">
      <p className="flex items-center gap-2 px-2 pb-2 pt-6 text-[0.625rem] font-bold uppercase tracking-[0.3em] text-rail-muted/60">
        <span aria-hidden="true" className="h-px w-3 bg-primary transition-colors duration-500" />
        {heading}
      </p>
      <div className="space-y-1">
        {items.map(({ to, labelKey, icon: Icon, count, alarming, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'group relative flex items-center gap-3 rounded-full py-1 pl-1 pr-2.5 text-sm transition-colors duration-200',
                'focus-visible:ring-offset-rail',
                isActive
                  ? 'font-semibold text-white'
                  : 'font-medium text-rail-foreground/60 hover:bg-white/[0.04] hover:text-rail-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/*
                  * The active plate is ONE element shared by every item (`layoutId`), so changing
                  * section slides it down the rail instead of blinking it off and on. Under
                  * reduced motion it is a plain span and simply appears.
                  */}
                {isActive ? (
                  reduce ? (
                    <span aria-hidden="true" className={PLATE} />
                  ) : (
                    <motion.span
                      aria-hidden="true"
                      layoutId="rail-active"
                      className={PLATE}
                      transition={{ type: 'spring', stiffness: 420, damping: 36 }}
                    />
                  )
                ) : null}

                {/* The ring. Hollow at rest, lit on hover, filled with the flavour when active —
                    the same three states as the "o" in the wordmark above it. */}
                <span
                  className={cn(
                    'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300',
                    isActive
                      ? 'scale-100 border-transparent bg-primary text-primary-foreground shadow-[0_0_18px_-2px_hsl(var(--primary))]'
                      : 'border-white/10 bg-white/[0.03] text-rail-muted group-hover:scale-105 group-hover:border-primary/60 group-hover:text-rail-foreground',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>

                <span className="relative truncate transition-transform duration-300 group-hover:translate-x-0.5">
                  {t(labelKey)}
                </span>
                <Counter value={count ?? 0} alarming={alarming} />
              </>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );

  return (
    <nav
      aria-label={t('common.appName')}
      className="relative flex h-full w-[16rem] shrink-0 flex-col overflow-hidden bg-rail text-rail-foreground"
    >
      {/* The cold-can dressing: flavour glow, condensation and bubbles, all behind the nav. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-80">
        <div className="bold-glow absolute inset-0" />
        <div className="bold-drops absolute inset-0 opacity-25 [mask-image:linear-gradient(to_top,black,transparent)]" />
        <Bubbles count={10} rise={260} seed={11} />
      </div>

      <div className="relative flex h-[4.5rem] shrink-0 items-center gap-2.5 px-5">
        <BoldLogo className="text-[2rem] text-white" />
        <span className="mt-1.5 rounded-full border border-white/10 px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.2em] text-rail-muted">
          admin
        </span>
      </div>
      {/* A hairline under the wordmark that carries the flavour colour in from the left. */}
      <div aria-hidden="true" className="relative mx-5 h-px bg-gradient-to-r from-primary via-white/10 to-transparent transition-colors duration-500" />

      <div className="rail-scroll relative flex-1 overflow-y-auto pb-4">
        {renderGroup(t('nav.sectionQueues'), queues)}
        {renderGroup(t('nav.sectionManage'), manage)}
        {renderGroup(t('nav.sectionSystem'), system)}
      </div>

      {/*
        * The foot of the rail: the can itself, standing in the glow, with the slogan. Purely
        * brand — it is hidden on short windows (`[@media(max-height:…)]`) so it can never push a
        * nav item out of reach.
        */}
      <div
        aria-hidden="true"
        className="relative mx-3 mb-3 hidden shrink-0 items-center gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 backdrop-blur-sm [@media(min-height:820px)]:flex"
      >
        <BoldCan className="bold-float text-[3.2px]" style={{ '--tilt': '8deg' } as CSSProperties} />
        <p className="font-display text-lg font-bold uppercase leading-[1.05] tracking-wide text-white/30">
          Be bright.
          <br />
          Be brave.
          <br />
          <span className="text-white">
            Be <span className="text-primary transition-colors duration-500">most.</span>
          </span>
        </p>
      </div>
    </nav>
  );
}
